import React, { useState, useEffect } from 'react';
import { Search, Tag, Heart, Copy, Check, ExternalLink, TrendingUp, ShoppingBag } from 'lucide-react';
import { signup, login, getSavedCodes, saveCode, unsaveCode, getCodes } from './api';
import SavedCodesSidebar from './SavedCodesSidebar';
 

function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [savedCodes, setSavedCodes] = useState(new Set());
  const [codes, setCodes] = useState([]);


// Load codes from database on mount
useEffect(() => {
  async function loadCodes() {
    try {
      const data = await getCodes();
      setCodes(data.codes);
    } catch (error) {
      console.error('Error loading codes:', error);
    }
  }
  loadCodes();
}, []);
const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleSave = async (id) => {
  if (!user) {
    alert('⚠️ Please login to save codes!');
    setShowLoginModal(true);
    return;
  }

  try {
    const newSaved = new Set(savedCodes);
    
    if (newSaved.has(id)) {
      // Unsave
      await unsaveCode(id);
      newSaved.delete(id);
      alert('💔 Code removed from saved!');
    } else {
      // Save
      await saveCode(id);
      newSaved.add(id);
      alert('❤️ Code saved successfully!');
    }
    
    setSavedCodes(newSaved);
  } catch (error) {
    alert('❌ Error: ' + (error.response?.data?.error || error.message));
  }
};

  const filteredCodes = codes.filter(code =>
    code.website.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.discount.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      
      <header className="bg-white shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-2 rounded-lg">
                <Tag className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  Nomical App
                </h1>
                <p className="text-xs text-gray-600">AI-Powered Discount Finder</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!user ? (
  <>
    <button 
      onClick={() => setShowLoginModal(true)}
      className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
    >
      Sign In
    </button>
    <button 
      onClick={() => setShowSignupModal(true)}
      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold"
    >
      Sign Up
    </button>
  </>
) : (
  <div className="flex items-center gap-3">
    <span className="text-gray-700">Hello, <strong>{user.name}</strong>!</span>
    <button 
  onClick={() => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    alert('✅ Logged out successfully!');
  }}
  className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
><button
  onClick={() => setSidebarOpen(true)}
  className="flex items-center gap-2 px-4 py-2 text-purple-600 hover:text-purple-700 font-medium"
>
  <Heart className="w-5 h-5" />
  Saved ({savedCodes.size})
</button>
  Logout
</button>
  </div>
)}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Find the <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">Best Discount Codes</span>
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            AI-powered scanning of 1,000+ YouTube channels. Get exclusive codes updated daily.
          </p>

          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by website, code, or discount..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none text-lg"
              />
            </div>
          </div>

          <div className="flex items-center justify-center gap-8 text-sm">
            <div className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-purple-500" />
              <span className="text-gray-700"><strong>{filteredCodes.length}</strong> active codes</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <span className="text-gray-700"><strong>1,234</strong> channels scanned</span>
            </div>
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-blue-500" />
              <span className="text-gray-700">Updated <strong>daily</strong></span>
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredCodes.map((code) => (
            <div key={code.id} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all p-6 border-l-4 border-purple-500">
              
              <div className="mb-3">
                {code.verified && (
                  <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold mb-2">
                    ✓ Verified
                  </span>
                )}
                <h3 className="font-bold text-lg text-gray-900">{code.website}</h3>
                <p className="text-xs text-gray-500">{code.category}</p>
              </div>

              <p className="text-2xl font-bold text-purple-600 mb-3">{code.discount}</p>

              <div className="mb-4">
                <div className="flex items-center gap-2 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-3 border-2 border-dashed border-purple-300">
                  <code className="flex-1 font-mono font-bold text-lg text-gray-900">{code.code}</code>
                  <button
                    onClick={() => copyToClipboard(code.code, code.id)}
                    className="p-2 hover:bg-white rounded transition-colors"
                  >
                    {copiedId === code.id ? (
                      <Check className="w-5 h-5 text-green-600" />
                    ) : (
                      <Copy className="w-5 h-5 text-purple-600" />
                    )}
                  </button>
                </div>
              </div>

              <button
                onClick={() => toggleSave(code.id)}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all mb-3 ${
                  savedCodes.has(code.id)
                    ? 'bg-red-100 text-red-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Heart className={`w-5 h-5 ${savedCodes.has(code.id) ? 'fill-current' : ''}`} />
                <span className="font-medium">
                  {savedCodes.has(code.id) ? 'Saved' : 'Save for Later'}
                </span>
              </button>

              <div className="mb-3 pb-3 border-b border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Found on channel:</p>
                <p className="font-semibold text-gray-900">{code.youtuber}</p>
              </div>

              <a
                href={code.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 mb-3"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="truncate">{code.videoTitle}</span>
              </a>

              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  <span>{code.uses.toLocaleString()} uses</span>
                </div>
                <span>{code.expiresIn}</span>
              </div>
            </div>
          ))}
        </div>

        {filteredCodes.length === 0 && (
          <div className="text-center py-12">
            <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No codes found</h3>
            <p className="text-gray-500">Try a different search term</p>
          </div>
        )}
      </div>{/* Signup Modal */}
{showSignupModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
      <button
        onClick={() => setShowSignupModal(false)}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
      >
        ×
      </button>
      
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Account</h2>
      <p className="text-gray-600 mb-6">Start saving with discount codes today!</p>
      
      <form onSubmit={async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const name = formData.get('name');
        const email = formData.get('email');
        const password = formData.get('password');
        
        try {
  const data = await signup(name, email, password);
  setUser(data.user);
  localStorage.setItem('token', data.token);
  setShowSignupModal(false);
  
  // Load saved codes
  try {
    const savedData = await getSavedCodes();
    const savedIds = new Set(savedData.savedCodes.map(c => c.code_id));
    setSavedCodes(savedIds);
  } catch (err) {
    console.log('No saved codes yet');
  }
  
  alert('✅ Account created successfully!');
} catch (error) {
          alert('❌ Signup failed: ' + (error.response?.data?.message || error.message));
        }
      }}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
          <input
            type="text"
            name="name"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
            placeholder="John Doe"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
          <input
            type="email"
            name="email"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
            placeholder="your@email.com"
          />
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
          <input
            type="password"
            name="password"
            required
            minLength="6"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
            placeholder="At least 6 characters"
          />
        </div>
        
        <button
          type="submit"
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700"
        >
          Create Account
        </button>
      </form>
      
      <p className="text-center text-sm text-gray-600 mt-6">
        Already have an account?{' '}
        <button
          onClick={() => {
            setShowSignupModal(false);
            setShowLoginModal(true);
          }}
          className="text-purple-600 hover:text-purple-700 font-semibold"
        >
          Sign in
        </button>
      </p>
    </div>
  </div>
)}{/* Login Modal */}
{showLoginModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
      <button
        onClick={() => setShowLoginModal(false)}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
      >
        ×
      </button>
      
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h2>
      <p className="text-gray-600 mb-6">Sign in to access your saved codes</p>
      
      <form onSubmit={async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const email = formData.get('email');
        const password = formData.get('password');
        
        try {
  const data = await login(email, password);
  setUser(data.user);
  localStorage.setItem('token', data.token);
  setShowLoginModal(false);
  
  // Load saved codes
  try {
    const savedData = await getSavedCodes();
    const savedIds = new Set(savedData.savedCodes.map(c => c.code_id));
    setSavedCodes(savedIds);
  } catch (err) {
    console.log('No saved codes yet');
  }
  
  alert('✅ Logged in successfully!');
} catch (error) {
          alert('❌ Login failed: ' + (error.response?.data?.message || error.message));
        }
      }}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
          <input
            type="email"
            name="email"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
            placeholder="your@email.com"
          />
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
          <input
            type="password"
            name="password"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
            placeholder="Your password"
          />
        </div>
        
        <button
          type="submit"
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700"
        >
          Sign In
        </button>
      </form>
      
      <p className="text-center text-sm text-gray-600 mt-6">
        Don't have an account?{' '}
        <button
          onClick={() => {
            setShowLoginModal(false);
            setShowSignupModal(true);
          }}
          className="text-purple-600 hover:text-purple-700 font-semibold"
        >
          Sign up
        </button>
      </p>
    </div>
  </div>
)}
{/* Saved Codes Sidebar */}
      <SavedCodesSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        savedCodes={savedCodes}
        allCodes={codes}
        onUnsave={toggleSave}
        onCopy={copyToClipboard}
        copiedId={copiedId}
      />
    </div>
  );
}

export default App;
// Build trigger
// Force deploy
